import React, { useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AppSettings } from '../../config/app-settings.js';
import Breadcrumb from '../../components/general/breadcrumb';

function ModellingDocumentation() {
  const context = useContext(AppSettings);

  useEffect(() => {
    context.handleSetAppContentClass('');
    context.handleSetAppSidebarTwo(false);
    context.handleSetAppSidebarWide(false);
    context.handleSetAppSidebarNone(true);
    context.handleSetAppTopMenu(true);

    return () => {
      context.handleSetAppSidebarTwo(false);
      context.handleSetAppSidebarWide(false);
      context.handleSetAppSidebarNone(false);
      context.handleSetAppTopMenu(false);
    };
  }, []);

  return (
    <>
      {/* Breadcrumb */}
      <div className="row">
        <div className="col-sm-12">
          <Breadcrumb />
        </div>
      </div>

      {/* Page Header */}
      <div className="row">
        <div className="col-sm-12">
          <h1 className="page-header mb-3">
            <i className="fa fa-layer-group me-2"></i>
            MODELLING PANELS DOCUMENTATION
          </h1>
          <p className="text-gray-600 mb-4">
            Complete guide to managing traffic analysis zones and network features using the Model Mapping interface.
          </p>
        </div>
      </div>

      {/* Module Overview */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading bg-primary">
              <h4 className="panel-title">Module Overview</h4>
            </div>
            <div className="panel-body">
              <div className="row">
                <div className="col-md-8">
                  <h5 className="text-black-900 mb-3">Key Features</h5>
                  <ul className="mb-0">
                    <li className="text-black mb-2">
                      Interactive map with layer controls
                    </li>
                    <li className="text-black mb-2">
                      Traffic Analysis Zone (TAZ) visualization
                    </li>
                    <li className="text-black mb-2">
                      Feature version history tracking
                    </li>
                    <li className="text-black mb-2">
                      Edit and commit changes to network features
                    </li>
                    <li className="text-black mb-2">
                      Supervision workflow for approvals
                    </li>
                    <li className="text-black mb-2">
                      Real-time map interactions
                    </li>
                  </ul>
                </div>
                <div className="col-md-4">
                  <div className="bg-gray-900 rounded p-4 text-center">
                    <i className="fa fa-layer-group fa-4x text-primary mb-3"></i>
                    <Link
                      to="/model-mapping-panels/model-mapping/"
                      className="btn btn-primary btn-sm w-100"
                    >
                      Open Model Mapping <i className="fa fa-arrow-right ms-2"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Getting Started */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-play-circle me-2"></i>
                Getting Started
              </h4>
            </div>
            <div className="panel-body">
              <div className="text-black mb-3">
                Introduction to the Model Mapping interface.
              </div>

              <div className="ms-3">
                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Accessing Model Mapping
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Navigate to Modelling Panels → Model Mapping from the main menu to open the interactive map interface.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Interface Overview
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    The Model Mapping page consists of three main components:
                  </p>
                  <ul className="ms-4 text-black">
                    <li className="mb-1">Interactive Map - Center panel showing geographic data</li>
                    <li className="mb-1">Layer Controls - Sidebar for managing visible layers</li>
                    <li className="mb-1">Feature Details - Right panel showing selected feature information</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Working with the Map */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-map me-2"></i>
                Working with the Map
              </h4>
            </div>
            <div className="panel-body">
              <div className="text-black mb-3">
                How to navigate and interact with map features.
              </div>

              <div className="ms-3">
                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Map Navigation
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Use standard map controls to explore the network:
                  </p>
                  <ul className="ms-4 text-black">
                    <li className="mb-1">Zoom in/out using the +/- buttons or mouse wheel</li>
                    <li className="mb-1">Pan by clicking and dragging</li>
                    <li className="mb-1">Click on any feature to view its details</li>
                  </ul>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Layer Management
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Control which data layers are visible on the map using the layer controls in the sidebar. Toggle layers on/off to focus on specific data.
                  </p>
                  <div className="alert alert-info ms-4 mt-2" role="alert">
                    <i className="fa fa-info-circle me-2"></i>
                    <strong>Note:</strong> Some layers may only be visible at certain zoom levels for performance reasons.
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Selecting Features
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Click on any TAZ or network feature to select it. Selected features will highlight, and their details will appear in the right panel.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Editing Features */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-edit me-2"></i>
                Editing Features
              </h4>
            </div>
            <div className="panel-body">
              <div className="text-black mb-3">
                How to modify network feature properties.
              </div>

              <div className="ms-3">
                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Enable Editing
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    To edit a feature:
                  </p>
                  <ol className="ms-4 text-black">
                    <li className="mb-1">Select the feature by clicking on it</li>
                    <li className="mb-1">View the feature details in the right panel</li>
                    <li className="mb-1">Click "Enable Editing" button</li>
                    <li className="mb-1">Edit icons will appear next to editable fields</li>
                    <li className="mb-1">Click the pencil icon to modify a specific field</li>
                    <li className="mb-1">Enter the new value and click the checkmark to save</li>
                  </ol>
                  <div className="alert alert-info ms-4 mt-2" role="alert">
                    <i className="fa fa-info-circle me-2"></i>
                    <strong>Note:</strong> Only users with appropriate permissions can edit features. Some fields may be read-only.
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Committing Changes
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    After making edits:
                  </p>
                  <ol className="ms-4 text-black">
                    <li className="mb-1">Review all your changes (marked with asterisks)</li>
                    <li className="mb-1">Enter your full name in the "Updated By" field</li>
                    <li className="mb-1">Add an optional comment describing your changes</li>
                    <li className="mb-1">Click "Commit All Changes" to save</li>
                  </ol>
                  <div className="alert alert-info ms-4 mt-2" role="alert">
                    <i className="fa fa-info-circle me-2"></i>
                    <strong>Note:</strong> Changes create a new version and require supervision approval before becoming active.
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Canceling Edits
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    To discard changes, click "Disable Editing" or click the reset icon next to individual fields. This will revert all unsaved changes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Version History */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-history me-2"></i>
                Version History
              </h4>
            </div>
            <div className="panel-body">
              <div className="text-black mb-3">
                Understanding feature versioning and history.
              </div>

              <div className="ms-3">
                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Viewing Version History
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Each feature maintains a complete history of changes. Use the version navigation arrows to browse through previous versions.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Version Information
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    For each version, you can view:
                  </p>
                  <ul className="ms-4 text-black">
                    <li className="mb-1">Version number</li>
                    <li className="mb-1">Date and time of creation</li>
                    <li className="mb-1">User who created the version</li>
                    <li className="mb-1">Approval status (Approved/Pending)</li>
                    <li className="mb-1">Creation and approval comments</li>
                  </ul>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Current vs Historical Versions
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    The system distinguishes between the current active version and historical versions. A green badge indicates the current version.
                  </p>
                  <div className="alert alert-info ms-4 mt-2" role="alert">
                    <i className="fa fa-info-circle me-2"></i>
                    <strong>Note:</strong> You can only edit the current version. Historical versions are read-only.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Supervision & Approval */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-check-circle me-2"></i>
                Supervision & Approval
              </h4>
            </div>
            <div className="panel-body">
              <div className="text-black mb-3">
                How the approval workflow operates (Admin only).
              </div>

              <div className="ms-3">
                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Pending Versions
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    When changes are committed, they create a pending version that requires administrative approval before becoming the active version.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Approving Changes (Admins)
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Administrators can approve or decline pending versions:
                  </p>
                  <ol className="ms-4 text-black">
                    <li className="mb-1">Select a feature with pending changes</li>
                    <li className="mb-1">Review the modified fields</li>
                    <li className="mb-1">Enter your name and a comment</li>
                    <li className="mb-1">Toggle the approval switch (Approve/Decline)</li>
                    <li className="mb-1">Click "Submit Supervision" to finalize</li>
                  </ol>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Approved vs Declined
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Approved versions become the new current version. Declined versions remain in history but do not affect the active data.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tips & Best Practices */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <i className="fa fa-lightbulb me-2"></i>
                Tips & Best Practices
              </h4>
            </div>
            <div className="panel-body">
              <div className="text-black mb-3">
                Recommendations for effective model mapping.
              </div>

              <div className="ms-3">
                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Always Add Comments
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    When committing changes or approving versions, always provide clear comments explaining the reason for modifications. This creates an audit trail.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Review Before Committing
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    Double-check all edited fields before committing. The asterisk indicators help identify which fields have been modified.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Use Version History
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    If you need to understand why a feature has specific values, review its version history to see the progression of changes.
                  </p>
                </div>

                <div className="mb-3">
                  <h6 className="text-black-900 mb-2">
                    <i className="fa fa-angle-right text-primary me-2"></i>
                    Pending Features Indicator
                  </h6>
                  <p className="text-black ms-4 mb-2">
                    The sidebar selector shows a count of features with pending versions, making it easy to track which features need supervision.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div className="row">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading bg-gray-900">
              <h4 className="panel-title">
                <i className="fa fa-life-ring me-2"></i>
                Need Help?
              </h4>
            </div>
            <div className="panel-body">
              <div className="row text-center">
                <div className="col-md-12 mb-3">
                  <i className="fa fa-envelope fa-2x text-primary mb-2"></i>
                  <h5 className="text-black-900 mb-2">Contact Support</h5>
                  <p className="text-black fs-13px mb-2">
                    Email us for technical assistance
                  </p>
                  <a href="mailto:support@acuere.com" className="text-primary">
                    support@acuere.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ModellingDocumentation;